
import React, { Component } from 'react'
import { Card, notification, Space, Upload, Button, Empty, Collapse, Table, Typography, Tabs } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import Http from '../../../utils/http'
import Cookies from 'universal-cookie'
import ExcelJS from 'exceljs'
import moment from 'moment'
import './tabs.css'

const { Panel } = Collapse
const { Title } = Typography


class WeightBalance extends Component {
  constructor(props) {
    super(props)
    this.state = {
      data: [],
      env: [],
      fuel: [],
      tab: 'oew',
      timezone: new Cookies().get('timezone'),
      activeAircraftKeys: []
    }
  }

  componentDidMount() {
    this.loadInitialData()
  }

  loadInitialData = async () => {
    try {
      const [data, env, fuel] = await Promise.all([
        this.getData('/w_b/oew'),
        this.getData('/w_b/env'),
        this.getData('/w_b/fuel')
      ])

      this.setState({
        data,
        env,
        fuel
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching initial weight and balance data:', error)
    }
  }

  getData = async (endpoint) => {
    const response = await Http.get(endpoint)
    return response?.data?.data || []
  }

  fetchData = async () => {
    try {
      const data = await this.getData('/w_b/oew')
      this.setState({ data })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  fetchEnv = async () => {
    try {
      const env = await this.getData('/w_b/env')
      this.setState({ env })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  fetchFuel = async () => {
    try {
      const fuel = await this.getData('/w_b/fuel')
      this.setState({ fuel })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    }
  }

  handleUpload = async (endpoint, file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await Http.post(
        endpoint,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'station': this.state.station
          }
        }
      )

      notification.destroy()

      if (response?.status === 200) {
        notification.success({
          message: 'Success',
          description: response?.data?.message,
        })
        await this.fetchData()
      } else {
        notification.error({
          message: 'Failed',
          description: response?.data?.message,
        })
      }
    } catch (err) {
      notification.destroy()
      notification.error({
        message: 'Failed',
        description: err?.response?.data?.message || 'Something went wrong',
      })
    }

    return false
  }

  getAircraftPanelKey = (item, index) => `${item?.type || 'type'}-${index}`

  getAircraftPanelKeys = (data = []) => data.map((item, index) => this.getAircraftPanelKey(item, index))

  handleAircraftAccordionChange = (keys) => {
    this.setState({ activeAircraftKeys: Array.isArray(keys) ? keys : keys ? [keys] : [] })
  }

  toggleAllAircraft = () => {
    const allKeys = this.getAircraftPanelKeys(this.state.data)
    const isAllExpanded = allKeys.length > 0 && allKeys.every((key) => this.state.activeAircraftKeys.includes(key))
    this.setState({ activeAircraftKeys: isAllExpanded ? [] : allKeys })
  }

  getColumnTitle = (key) => key.charAt(0).toUpperCase() + key.slice(1).split('_').join(' ')

  getExtraColumnNames = (rows = []) => {
    const hiddenColumns = ['ship', 'dom_oew_adj_wt', 'intl_oew_adj_wt', 'config']
    const extraColumns = []

    rows.forEach((value) => {
      Object.keys(value || {}).forEach((key) => {
        if (!hiddenColumns.includes(key) && !extraColumns.includes(key)) {
          extraColumns.push(key)
        }
      })
    })

    return extraColumns
  }

  getOewColumns = (extraColumns = []) => {
    const baseColumns = [
      {
        title: 'Ship',
        dataIndex: 'ship',
        key: 'ship',
        className: 'wb-col-ship',
        width: 90,
        render: (value) => value || '-'
      },
      {
        title: 'Dom OEW Adj Wt',
        dataIndex: 'dom_oew_adj_wt',
        key: 'dom_oew_adj_wt',
        className: 'wb-col-dom',
        width: 150,
        render: (value) => value || '-'
      },
      {
        title: 'Intl OEW Adj Wt',
        dataIndex: 'intl_oew_adj_wt',
        key: 'intl_oew_adj_wt',
        className: 'wb-col-intl',
        width: 150,
        render: (value) => value || '-'
      },
      {
        title: 'Config',
        dataIndex: 'config',
        key: 'config',
        className: 'wb-col-config',
        width: 120,
        render: (value) => <span className='wb-config-pill'>{value || '-'}</span>
      }
    ]

    const dynamicColumns = extraColumns.map((column) => ({
      title: this.getColumnTitle(column),
      dataIndex: column,
      key: column,
      className: 'wb-col-extra',
      width: 120,
      render: (value) => value || '-'
    }))

    return [...baseColumns, ...dynamicColumns]
  }

  exportExcel = async (type) => {
    let date = moment().tz(this.state.timezone).format('YYYY-MM-DD')
    if(type === 'oew') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let oewData = item?.oew || []
        let name = item?.type+' oew'
        const ws = wb.addWorksheet(name)
        const columns = Object.keys(oewData[0] || {}).map((key) => ({
          header: this.getColumnTitle(key),
          width: 20
        }))
        ws.columns = columns
        oewData.forEach((row) => {
          ws.addRow([
            ...Object.keys(row).map(key => row[key])
          ])
        })
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `oew_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    } else if(type === 'pax') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let summerData = item?.pax_wts_summer || []
        let winterData = item?.pax_wts_winter || []
        if(!Array.isArray(summerData)) {
          Object.keys(summerData).forEach((key) => {
            let name = item?.type+' pax wts summer '+key?.split('_')[1]+' '+key?.split('_')[0]+' '+(key?.split('_')[2] || '')
            const ws = wb.addWorksheet(name)
            ws.columns = [
              {
                header: 'Pax',
                width: 15
              },
              {
                header: 'Cabin 1 Adj Wt',
                width: 20
              },
              {
                header: 'Cabin 2 Adj Wt',
                width: 20
              },
              {
                header: 'Cabin 3 Adj Wt',
                width: 20
              }
            ]
            summerData[key].forEach((row) => {
              ws.addRow([
                row?.pax_count,
                row?.cabin_1_adj_wt,
                row?.cabin_2_adj_wt,
                row?.cabin_3_adj_wt
              ])
            })
          })
          Object.keys(winterData).forEach((key) => {
            let name = item?.type+' pax wts winter '+key?.split('_')[1]+' '+key?.split('_')[0]+' '+(key?.split('_')[2] || '')
            const ws = wb.addWorksheet(name)
            ws.columns = [
              {
                header: 'Pax',
                width: 15
              },
              {
                header: 'Cabin 1 Adj Wt',
                width: 20
              },
              {
                header: 'Cabin 2 Adj Wt',
                width: 20
              },
              {
                header: 'Cabin 3 Adj Wt',
                width: 20
              }
            ]
            winterData[key].forEach((row) => {
              ws.addRow([
                row?.pax_count,
                row?.cabin_1_adj_wt,
                row?.cabin_2_adj_wt,
                row?.cabin_3_adj_wt
              ])
            })
          })
        } else {
          let name = item?.type+' pax wts summer'
          const ws = wb.addWorksheet(name)
          ws.columns = [
            {
              header: 'Pax',
              width: 15
            },
            {
              header: 'Cabin 1 Adj Wt',
              width: 20
            },
            {
              header: 'Cabin 2 Adj Wt',
              width: 20
            },
            {
              header: 'Cabin 3 Adj Wt',
              width: 20
            }
          ]
          summerData.forEach((row) => {
            ws.addRow([
              row?.pax_count,
              row?.cabin_1_adj_wt,
              row?.cabin_2_adj_wt,
              row?.cabin_3_adj_wt
            ])
          })
          name = item?.type+' pax wts winter'
          const ws2 = wb.addWorksheet(name)
          ws2.columns = [
            {
              header: 'Pax',
              width: 15
            },
            {
              header: 'Cabin 1 Adj Wt',
              width: 20
            },
            {
              header: 'Cabin 2 Adj Wt',
              width: 20
            },
            {
              header: 'Cabin 3 Adj Wt',
              width: 20
            }
          ]
          winterData.forEach((row) => {
            ws2.addRow([
              row?.pax_count,
              row?.cabin_1_adj_wt,
              row?.cabin_2_adj_wt,
              row?.cabin_3_adj_wt
            ])
          })
        }
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `pax_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    } else if(type === 'pallet') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let palletData = item?.pallet || []
        if(!Array.isArray(palletData)) {
          Object.keys(palletData).forEach((key) => {
            let name = item?.type+' pallet '+key?.split('_')[0]+'s '+key?.split('_')[1]+'&'+key?.split('_')[2]
            const ws = wb.addWorksheet(name)
            const columns = Object.keys(palletData[key][0] || {}).map((key) => {
              if(key == 'act_wgt')
              {
                return {
                  header: 'Wt',
                  width: 15
                }
              }else
              {
                return {
                  header: key.replaceAll('p','')+'P',
                  width: 20
                }
              }
            })
            ws.columns = columns
            palletData[key].forEach((row) => {
              ws.addRow([
                ...Object.keys(row).map(k => row[k])
              ])
            })
          })
        } else {
          let name = item?.type+' pallet'
          const ws = wb.addWorksheet(name)
          const columns = Object.keys(palletData[0] || {}).map((key) => {
            if(key == 'act_wgt')
            {
              return {
                header: 'Wt',
                width: 15
              }
            }else
            {
              return {
                header: key.replaceAll('p','')+'P',
                width: 20
              }
            }
          })
          ws.columns = columns
          palletData.forEach((row) => {
            ws.addRow([
              ...Object.keys(row).map(key => row[key])
            ])
          })
        }
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `pallet_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    } else if(type === 'cargo') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let cargoData = item?.cargo || []
        if(!Array.isArray(cargoData)) {
          Object.keys(cargoData).forEach((key) => {
            // bin_1_2
            let name = item?.type+' cargo '+key?.split('_')[0]+'s '+key?.split('_')[1]+'&'+key?.split('_')[2]
            const ws = wb.addWorksheet(name)
            const columns = Object.keys(cargoData[key][0] || {}).map((key) => {
              if(key == 'wt')
              {
                return {
                  header: 'Wt',
                  width: 15
                }
              }else
              {
                return {
                  header: key.split('_')[2]+'L/'+key.split('_')[2]+'R',
                  width: 20
                }
              }
            })
            ws.columns = columns
            cargoData[key].forEach((row) => {
              ws.addRow([
                ...Object.keys(row).map(key => row[key])
              ])
            })
          })
        } else {
          let name = item?.type+' cargo'
          const ws = wb.addWorksheet(name)
          const columns = Object.keys(cargoData[0] || {}).map((key) => {
            if(key == 'wt')
            {
              return {
                header: 'Wt',
                width: 15
              }
            }else
            {
              return {
                header: key.split('_')[2]+'L/'+key.split('_')[2]+'R',
                width: 20
              }
            }
          })
          ws.columns = columns
          cargoData.forEach((row) => {
            ws.addRow([
              ...Object.keys(row).map(key => row[key])
            ])
          })
        }
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `cargo_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    } else if(type === 'bulk') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let bulkData = item?.bulk || []
        let name = item?.type+' bulk bins'
        const ws = wb.addWorksheet(name)
        ws.columns = [
          {
            header: 'Wt',
            width: 15 
          },
          {
            header: 'Adj Wt',
            width: 20 
          },
        ]
        bulkData.forEach((row) => {
          ws.addRow([
            row?.wt,
            row?.adj_wt
          ])
        })
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `bulk_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    } else if(type === 'fuel') {
      const wb = new ExcelJS.Workbook()
      this.state.data.forEach((item) => {
        let fuelData = item?.fuel || []
        if(!Array.isArray(fuelData)) {
          Object.keys(fuelData).forEach((key) => {
            let name = item?.type+' '+key+' fuel tank'
            const ws = wb.addWorksheet(name)
            ws.columns = [
              {
                header: 'Wt',
                width: 15 
              },
              {
                header: 'Adj Wt',
                width: 20 
              },
            ]
            fuelData[key].forEach((row) => {
              ws.addRow([
                row?.wt,
                row?.adj_wt
              ])
            })
          })
        } else {
          let name = item?.type+' total fuel'
          const ws = wb.addWorksheet(name)
          ws.columns = [
            {
              header: 'Wt',
              width: 15 
            },
            {
              header: 'Adj Wt',
              width: 20 
            },
          ]
          fuelData.forEach((row) => {
            ws.addRow([
              row?.wt,
              row?.adj_wt
            ])
          })
        }
      })
      await wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `fuel_${date}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
    }
  }


  oew = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Operating Weight (OEW)</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('oew')}>
                Export
            </Button> 
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/oew', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No OEW records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const oewRows = item?.oew || []
                const extraColumns = this.getExtraColumnNames(oewRows)
                const panelKey = this.getAircraftPanelKey(item, index)
                const columns = this.getOewColumns(extraColumns)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        <Table
                          className='custom-table'
                          columns={columns}
                          dataSource={oewRows}
                          rowKey={(record, rowIndex) => `${record?.id || 'row'}-${rowIndex}`}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }
  pax = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Passenger Weight (PAX)</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('pax')}>
                Export
            </Button> 
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/pax', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Passenger Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const pax_wts_summer = item.pax_wts_summer || []
                const pax_wts_winter = item.pax_wts_winter || []
                const isArray = Array.isArray(pax_wts_summer) && Array.isArray(pax_wts_winter)
                const panelKey = this.getAircraftPanelKey(item, index)
                const columns = [
                  {
                    title: 'Pax Count',
                    dataIndex: 'pax_count',
                    key: 'pax_count',
                    className: 'wb-col-ship',
                    width: 90,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 1 Adj Wt',
                    dataIndex: 'cabin_1_adj_wt',
                    key: 'cabin_1_adj_wt',
                    className: 'wb-col-dom',
                    width: 150,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 2 Adj Wt',
                    dataIndex: 'cabin_2_adj_wt',
                    key: 'cabin_2_adj_wt',
                    className: 'wb-col-intl',
                    width: 150,
                    render: (value) => value
                  },
                  {
                    title: 'Cabin 3 Adj Wt',
                    dataIndex: 'cabin_3_adj_wt',
                    key: 'cabin_3_adj_wt',
                    className: 'wb-col-config',
                    width: 120,
                    render: (value) => value
                  }
                ]
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        <Title level={5} className='mb-0'>Summer</Title>
                        {isArray && (<Table
                          className='custom-table'
                          columns={columns}
                          dataSource={pax_wts_summer}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pax_wts_summer).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={columns}
                              dataSource={pax_wts_summer[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className='wb-grid-shell'>
                        <Title level={5} className='mb-0'>Winter</Title>
                        {isArray && (<Table
                          className='custom-table'
                          columns={columns}
                          dataSource={pax_wts_winter}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pax_wts_winter).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={columns}
                              dataSource={pax_wts_winter[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  getColumns = (object) => {
    let columns = Object.keys(object).map((key) => ({
      title: this.getColumnTitle(key),
      dataIndex: key,
      key: key,
      render: (value) => value
    }))
    columns = columns.filter((col) => col.dataIndex !== 'id')
    return columns
  }
  pallet = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Pallet Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('pallet')}>
                Export
            </Button> 
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/pallet', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Pallet Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const pallet = item.pallet || []
                const isArray = Array.isArray(pallet)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(pallet[0] || {})}
                          dataSource={pallet}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(pallet).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(pallet[key][0] || {})}
                              dataSource={pallet[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  cargo = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Cargo Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('cargo')}>
                Export
            </Button> 
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/cargo', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Cargo Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const cargo = item.cargo || []
                const isArray = Array.isArray(cargo)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(cargo[0] || {})}
                          dataSource={cargo}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(cargo).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(cargo[key][0] || {})}
                              dataSource={cargo[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  bulk = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Bulk Weight</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('bulk')}>
              Export
            </Button>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/bulk', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Bulk Weight records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const bulk = item.bulk || []
                const isArray = Array.isArray(bulk)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(bulk[0] || {})}
                          dataSource={bulk}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(bulk).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(bulk[key][0] || {})}
                              dataSource={bulk[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }
  fuel = () => {
    const { data, activeAircraftKeys } = this.state
    const allPanelKeys = this.getAircraftPanelKeys(data)
    const isAllExpanded = allPanelKeys.length > 0 && activeAircraftKeys.length === allPanelKeys.length

    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Fuel</Title>
          <Space direction='horizontal' size={0} className='ml-auto d-flex'>
            <Button className='custom-button' onClick={() => this.exportExcel('fuel')}>
              Export
            </Button>
            <Upload accept='.xlsx,.xls' showUploadList={false} beforeUpload={(file) => this.handleUpload('/w_b/fuel', file)}>
              <Button type='link' className='link-btn-dod uld-upload wb-upload-btn' icon={<UploadOutlined style={{ fontSize: 16 }} />}>Upload</Button>
            </Upload>
            <Button className='custom-button' onClick={this.toggleAllAircraft}>
              {isAllExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>

        {data.length === 0 ? (
          <Card className='wb-type-card'>
            <Empty description='No Fuel records found' />
          </Card>
        ) : (
          <div className='wb-card'>
            <Collapse
              activeKey={activeAircraftKeys}
              onChange={this.handleAircraftAccordionChange}
              className='wb-accordion'
              bordered={false}
            >
              {data.map((item, index) => {
                const fuel = item.fuel || []
                const isArray = Array.isArray(fuel)
                const panelKey = this.getAircraftPanelKey(item, index)
                return (
                  <Panel key={panelKey} header={`Aircraft Type: ${item?.type || '-'}`} className='wb-accordion-panel'>
                    <div className='table-responsive wb-type-table-wrap'>
                      <div className='wb-grid-shell'>
                        {isArray && (<Table
                          className='custom-table'
                          columns={this.getColumns(fuel[0] || {})}
                          dataSource={fuel}
                          pagination={false}
                          size='small'
                          locale={{ emptyText: 'No rows available' }}
                        />)}
                        {!isArray && Object.keys(fuel).map((key) => (
                          <div key={key}>
                            <Title level={5} className='mb-0'>{key?.toUpperCase()?.replaceAll('_', ' ')}</Title>
                            <Table
                              className='custom-table'
                              columns={this.getColumns(fuel[key][0] || {})}
                              dataSource={fuel[key]}
                              pagination={false}
                              size='small'
                              locale={{ emptyText: 'No rows available' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        )}
      </div>
    )
  }

  env = () => {
    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Opti Env</Title>
        </div>
        <div className=''>
          <div className='table-responsive'>
            <div className='wb-grid-shell table-outer'>
              <Table
                className='custom-table wb-table'
                columns={this.getColumns(this.state.env?.[0] || {})}
                dataSource={this.state.env || []}
                pagination={false}
                size='small'
                locale={{ emptyText: 'No rows available' }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  efuel = () => {
    return (
      <div className='wb-panel'>
        <div className='wb-panel-head'>
          <Title level={5} className='mb-0'>Opti Fuel</Title>
        </div>
        <div className=''>
          <div className='table-responsive'>
            <div className='wb-grid-shell table-outer'>
              <Table
                className='custom-table wb-table'
                columns={this.getColumns(this.state.fuel?.[0] || {})}
                dataSource={this.state.fuel || []}
                pagination={false}
                size='small'
                locale={{ emptyText: 'No rows available' }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const tabItems = [
      {
        key: 'oew',
        label: <span className='sider-menu-item'>Operating Empty Weight</span>,
        children: this.oew()
      },
      {
        key: 'pax',
        label: <span className='sider-menu-item'>Passenger Weight</span>,
        children: this.pax()
      },
      {
        key: 'pallet',
        label: <span className='sider-menu-item'>Pallet Weight</span>,
        children: this.pallet()
      },
      {
        key: 'cargo',
        label: <span className='sider-menu-item'>Cargo Weight</span>,
        children: this.cargo()
      },
      {
        key: 'bulk',
        label: <span className='sider-menu-item'>Bulk Weight</span>,
        children: this.bulk()
      },
      {
        key: 'fuel',
        label: <span className='sider-menu-item'>Fuel Weight</span>,
        children: this.fuel()
      },
      {
        key: 'env',
        label: <span className='sider-menu-item'>OPTI ENV</span>,
        children: this.env()
      },
      {
        key: 'efuel',
        label: <span className='sider-menu-item'>ENV Fuel</span>,
        children: this.efuel()
      }
    ]

    return (
      <div>
        <Card className='custom-card h-100 mess-card'>
          <Tabs
            tabPosition='left'
            className='vertical-tabs'
            activeKey={this.state.tab}
            onChange={(key) => this.setState({ tab: key })}
            items={tabItems}
          />
        </Card>
      </div>
      
    )
  }
}


export default WeightBalance